<script setup lang="ts" generic="T extends Record<string, string>">
import { computed, inject, ref } from 'vue';
import { FLOAT_PATTERN, INT_PATTERN, NxValidatedTextInput } from './../Inputs';
import type { InlineIconProps } from './../Inputs/internal';
import { defaultEditCellFocusRef, onEditCellKeydown, PartialOrNull } from './internal';
import { i18nInjectionKey } from '../internal';
import { coerceNumberCell, numberCellEditText, type NumberColumnKind } from './numberCell';
import { default as NxTableColumn, type NxTableColumnProps } from './NxTableColumn.vue';

const props = withDefaults(
  defineProps<
    Omit<NxTableColumnProps<T, keyof T, number>, 'toCsvString'> & {
      /** value that will be used for the csv export */
      toCsvString?: NxTableColumnProps<T, keyof T, number>['toCsvString'];
      /** icons to show within the input cells */
      icons?: InlineIconProps[];
      /**
       * Whether the column holds integers or fractional values. Drives both the
       * input pattern (integer forbids a decimal point) AND how an edit/create
       * is coerced on write: an `integer` cell emits a JS number, a `decimal`
       * cell keeps its string so precision is preserved. Defaults to `decimal`.
       */
      kind?: NumberColumnKind;
    }
  >(),
  {
    summary: 'sum',
    filterable: true,
    resizeable: true,
    reorderable: true,
    sortable: true,
    editable: true,
    defaultVisible: true,
    layout: 'row',
    toCsvString: undefined,
    icons: undefined,
    kind: 'decimal',
  }
);

// The app's locale, for the editor's decimal separator; absent in bare tests.
const i18n = inject(i18nInjectionKey, null);

// Integer columns forbid a decimal point in the input; decimals allow one.
const inputPattern = computed(() => (props.kind === 'integer' ? INT_PATTERN : FLOAT_PATTERN));

function toCsvString_(rowData: T, rowIndex: number) {
  if (props.toCsvString) {
    return props.toCsvString(rowData, rowIndex);
  }
  if (rowData[props.columnId] === null) {
    return '';
  }
  return `${rowData[props.columnId]}`;
}

const filterCellModel = ref<string>('');
const createCellModel = ref<string>('');

function onQueryLoaded(value: (number | string)[]) {
  filterCellModel.value = value.length > 0 ? `${value[0]}` : '';
}
</script>

<template>
  <!-- @vue-generic {T, keyof T, number} -->
  <NxTableColumn
    v-bind="{
      ...props,
      filterKind: props.filterKind ?? 'number',
      onQueryLoaded: props.onQueryLoaded ? props.onQueryLoaded : onQueryLoaded,
      toCsvString: toCsvString_,
    }"
  >
    <template v-if="$slots.summaryCell" #summaryCell="summaryProps">
      <slot name="summaryCell" v-bind="summaryProps"></slot>
    </template>
    <template v-if="$slots.headerCell" #headerCell>
      <slot name="headerCell"></slot>
    </template>
    <template
      #filterCell="{
        setFilterValue,
        disabled,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="filterCell"
        v-bind="{
          setFilterValue,
          disabled,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <NxValidatedTextInput
          :ref="defaultEditCellFocusRef(registerFocus)"
          v-model:model-value="filterCellModel"
          height="xs"
          placeholder=""
          :disabled="disabled"
          inputmode="decimal"
          :pattern="inputPattern"
          :icons="props.icons"
          @update:model-value="
            (value: string) => {
              // Filter values are strings by the table's filter contract; the
              // column kind drives the input pattern above, so here we only
              // normalize the locale comma to a dot.
              const v = value.replace(',', '.');
              setFilterValue(v !== '' ? [v] : []);
            }
          "
          @keydown="
            (e: KeyboardEvent) =>
              onEditCellKeydown(e, {
                focusAbove,
                focusBelow,
                focusLeft,
                focusRight,
                inputType: 'text',
              })
          "
        ></NxValidatedTextInput>
      </slot>
    </template>
    <template #cell="{ rowData, rowIndex }">
      <slot name="cell" v-bind="{ rowData, rowIndex }">
        <span class="one-line">{{ toCsvString_(rowData, rowIndex) }}</span>
      </slot>
    </template>
    <template #createCell="{ setPartialRowData }">
      <slot name="createCell" v-bind="{ setPartialRowData }">
        <NxValidatedTextInput
          :model-value="createCellModel"
          :placeholder="`${props.displayName ?? (props.columnId as string)}`"
          inputmode="decimal"
          :pattern="inputPattern"
          :icons="props.icons"
          @update:model-value="
            (value: string) => {
              setPartialRowData({
                [props.columnId]: coerceNumberCell(value, props.kind),
              } as unknown as PartialOrNull<T>);
            }
          "
        ></NxValidatedTextInput>
      </slot>
    </template>
    <template
      #editCell="{
        rowData,
        rowIndex,
        onEdit,
        registerFocus,
        focusAbove,
        focusBelow,
        focusLeft,
        focusRight,
      }"
    >
      <slot
        name="editCell"
        v-bind="{
          rowData,
          rowIndex,
          onEdit,
          registerFocus,
          focusAbove,
          focusBelow,
          focusLeft,
          focusRight,
        }"
      >
        <div class="nx-ghost-cell">
          <NxValidatedTextInput
            v-if="rowData[props.columnId] !== undefined"
            :ref="defaultEditCellFocusRef(registerFocus)"
            :model-value="numberCellEditText(rowData[props.columnId], i18n?.locale.value)"
            :model-modifiers="{ lazy: true }"
            height="xs"
            inputmode="decimal"
            :pattern="inputPattern"
            :icons="props.icons"
            @update:model-value="
              (value: string) => {
                onEdit({
                  // Coerce by column kind: an integer cell emits a JS number, a
                  // decimal cell keeps its string so precision is preserved
                  // (I-105). The old Number() conversion here forced every numeric
                  // column through a float.
                  [props.columnId]: coerceNumberCell(value, props.kind),
                } as unknown as Partial<T>);
              }
            "
            @keydown="
              (e: KeyboardEvent) =>
                onEditCellKeydown(e, {
                  focusAbove,
                  focusBelow,
                  focusLeft,
                  focusRight,
                  inputType: 'text',
                })
            "
          />
          <!-- TODO: move this in own rabatt type or so -->
          <!-- eslint-disable-next-line @intlify/vue-i18n/no-raw-text -->
          <div v-else class="one-line" style="padding-top: 0.575em">&ndash;</div>
        </div>
      </slot>
    </template>
  </NxTableColumn>
</template>

<style scoped>
.one-line {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  /* Cut long values with an ellipsis, never mid-letter. */
  text-overflow: ellipsis;
}
</style>
